import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import CLocalizedTextStringStatic from '../../../components/CLocalizedTextStringStatic';
import CPageModule from '../../../components/CPageModule';
import CPageModulePaginatedTable from '../../../components/CPageModulePaginatedTable';
import CStatefulContainer from '../../../components/CStatefulContainer';
import Page from '../../../components/layout/Page';
import { updateStateAttribute, updateStateResponseStatefulAttribute, useStateExtended } from '../../../helpers/helper';
import { useAppSelector } from '../../../hooks/hooks';
import { type IAugmentedBackendResponse } from '../../../models/IAugmentedBackendResponse';
import type { IRequestParams } from '../../../models/IRequestParams';
import type { IResponse } from '../../../models/IResponse';
import { type ISortingMeta } from '../../../models/ISortingMeta';
import { type TPandasTable } from '../../../models/TPandasTable';
import SvUserManagementCoreService from '../../../services/SvUserManagementCoreService';

interface State {
  snapshot: IResponse<IAugmentedBackendResponse<TPandasTable>> & IRequestParams;
}

export default function PSignUpIndexPage() {
  const { locale } = useAppSelector((state) => state.dashboard);
  const [searchParams, setSearchParams] = useSearchParams();
  const allSignUpRequestsTitle = CLocalizedTextStringStatic('signUp.all.requests.title');

  const defaultSorting: ISortingMeta = { sortCol: 0, ascending: 'descending' };

  const [state, setState, getState] = useStateExtended<State>({
    snapshot: {
      state: { isLoading: true, isError: false },
      params: { sorting: defaultSorting }
    }
  });

  function toBackendSorting(sorting: ISortingMeta): ISortingMeta {
    const uiIndex = Number(sorting.sortCol);

    // If the user did not change sorting yet, keep initial backend index 0
    const isInitialLoad = sorting.sortCol === 0 && sorting.ascending === 'descending';

    if (isInitialLoad) {
      return sorting;
    }

    return {
      sortCol: (uiIndex + 1) as any,
      ascending: sorting.ascending
    };
  }

  const fetchSignUpRequestsPage = async (page: number, sorting?: ISortingMeta, search?: string) => {
    const effective = sorting ?? defaultSorting;
    const backendSorting = toBackendSorting(effective);

    await updateStateResponseStatefulAttribute<State, typeof state.snapshot.data>(
      { snapshot: (await getState()).snapshot },
      setState,
      async () =>
        await SvUserManagementCoreService.getAllSignUpRequests({
          page,
          sorting: backendSorting,
          search
        })
    );
    updateStateAttribute<State>(
      { snapshot: { ...(await getState()).snapshot, params: { sorting, search } } },
      setState
    );
  };

  useEffect(() => {
    const params = {
      sortCol: Number.parseInt(searchParams.get('sortCol') ?? defaultSorting.sortCol.toString()),
      ascending: (searchParams.get('ascending') as unknown as ISortingMeta['ascending']) ?? 'descending'
    };
    const page = Number.parseInt(searchParams.get('page') ?? '1');
    const search = searchParams.get('search') ?? undefined;

    (async () => {
      await fetchSignUpRequestsPage(page, params, search);
    })();
  }, [locale]);

  const rawResults = state.snapshot.data?.results;

  const resultsWithoutFirstColumn: TPandasTable | undefined = rawResults
    ? Object.fromEntries(Object.entries(rawResults).slice(1))
    : undefined;

  const tableData = state.snapshot.data
    ? { ...state.snapshot.data, results: resultsWithoutFirstColumn ?? state.snapshot.data.results }
    : undefined;

  const userIDLocalizedColumnName = Object.keys(state.snapshot.data?.results ?? {})[0];

  return (
    <Page title={allSignUpRequestsTitle}>
      <CPageModule>
        <h1>{allSignUpRequestsTitle}</h1>
      </CPageModule>
      <CPageModule fullWidth style={{ paddingTop: 0 }}>
        <CStatefulContainer submodule apiRequestState={state.snapshot.state} showContent={!!state.snapshot.data}>
          <CPageModulePaginatedTable
            state={state.snapshot.state}
            data={tableData}
            params={state.snapshot.params}
            searchBarName={'inquiryLinks'}
            searchBarPlaceholder={CLocalizedTextStringStatic('signUpRequestSearchPlaceholder')}
            colSpanArr={[1, 1, 1, 2, 0.5]}
            colAlignmentArr={['left', 'left', 'left', 'left', 'center']}
            colFormatArr={[undefined, undefined, undefined, undefined, undefined, undefined]}
            onChange={async (page, sorting, search) => {
              await fetchSignUpRequestsPage(page, sorting, search);
              searchParams.set('page', page.toString());
              searchParams.set('sortCol', sorting.sortCol.toString());
              searchParams.set('ascending', sorting.ascending);
              searchParams.set('search', search);
              setSearchParams(searchParams);
            }}
            rowLinks={Object.values(state.snapshot.data?.results?.[userIDLocalizedColumnName] ?? []).map((e) => `${e}`)}
          />
        </CStatefulContainer>
      </CPageModule>
    </Page>
  );
}
